import React, { useEffect, useState } from 'react';
import { ArrowRight, Mail, Mic } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  fetchPlanningDashboardData,
  type PlanningDashboardData,
} from '../../lib/planningDashboard';
import SubscriberTotalTrendChart from './SubscriberTotalTrendChart';

function loadErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim()) return error.message;
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  return 'Failed to load planning stats';
}

function Metric({
  value,
  label,
  emphasize = false,
}: {
  value: string | number;
  label: string;
  emphasize?: boolean;
}) {
  return (
    <div className={emphasize ? '' : 'pt-2 mt-2 border-t border-gray-200/80 dark:border-gray-700/80'}>
      <div
        className={`font-bold tabular-nums text-gray-900 dark:text-white leading-none ${
          emphasize ? 'text-2xl' : 'text-lg'
        }`}
      >
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      <div className="mt-1 text-[11px] text-gray-600 dark:text-gray-400">{label}</div>
    </div>
  );
}

const innerBoxClass =
  'rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 min-w-0';

const planningSidePanelClass = `${innerBoxClass} md:w-56 shrink-0 px-3 py-2.5`;

const PlanningDashboardRow: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<PlanningDashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void fetchPlanningDashboardData()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(loadErrorMessage(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3">
      {loading ? (
        <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">Loading planning panels…</div>
      ) : error ? (
        <div className="py-10 text-center text-sm text-rose-600 dark:text-rose-400">{error}</div>
      ) : data ? (
        <div className="flex flex-col md:flex-row gap-2 items-stretch">
          <button
            type="button"
            onClick={() => navigate('/transcripts')}
            className={`${planningSidePanelClass} text-left hover:bg-white dark:hover:bg-gray-800 transition-colors group`}
          >
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-1.5 min-w-0">
                <Mic className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">Transcripts</span>
              </div>
              <ArrowRight className="w-3.5 h-3.5 text-gray-400 group-hover:text-emerald-500 shrink-0" />
            </div>
            <Metric value={data.transcriptsLast14Days} label="in last 14 days" emphasize />
            <Metric value={data.transcriptsNeedProcessing} label="need indexing / processing" />
          </button>

          <button
            type="button"
            onClick={() => navigate('/outgoing-newsletters')}
            className={`${planningSidePanelClass} text-left hover:bg-white dark:hover:bg-gray-800 transition-colors group`}
          >
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-1.5 min-w-0">
                <Mail className="w-4 h-4 text-sky-500 shrink-0" />
                <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">Future content</span>
              </div>
              <ArrowRight className="w-3.5 h-3.5 text-gray-400 group-hover:text-emerald-500 shrink-0" />
            </div>
            <Metric value={data.contentScheduledFuture} label="scheduled ahead" emphasize />
            <p className="mt-2 text-[11px] text-gray-600 dark:text-gray-400 leading-snug">
              Newsletter sends with a future date — your forward plan.
            </p>
          </button>

          <div className={`${innerBoxClass} flex-1 min-w-0 self-stretch p-0 overflow-hidden flex flex-col`}>
            <SubscriberTotalTrendChart
              createdAtValues={data.subscriberCreatedAtValues}
              onViewAll={() => navigate('/subscribers')}
              className="h-full min-h-[7.5rem] flex-1"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PlanningDashboardRow;
